import { ColorPicker } from '@trimble/ui-components/solidjs';
import { isPointerEventInsideRoot } from '@trimble/solidjs-utils';
import {
  Component,
  createEffect,
  createSignal,
  onCleanup,
  Show,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import type { RgbColor } from 'solid-color';
import tinycolor from 'tinycolor2';
import {
  COLOR_PICKER_PANEL_HEIGHT_PX,
  COLOR_PICKER_PANEL_WIDTH_PX,
  computePaletteStopPickerPosition,
  toPickerHexColor,
  type PickerPosition,
} from './createPaletteStopColorPickerControls';

export interface PaletteStopColorPickerProps {
  readonly show: boolean;
  readonly anchorEl?: HTMLElement;
  readonly color: string;
  readonly testId: string;
  readonly useTransparency?: boolean;
  /** Live colour updates (e.g. transparency slider) — picker stays open. */
  onChange?(rgb: RgbColor): void;
  onSave(rgb: RgbColor): void;
  onClose(): void;
}

/**
 * Thin wrapper around the shared `ColorPicker` from `@trimble/ui-components`:
 * portals it and positions it beside a swatch anchor. All colour-picking
 * behaviour is delegated to the shared component.
 *
 * Closes on outside click (document `mousedown` / `touchstart`), so opening
 * another swatch's picker automatically dismisses this one — no shared group
 * coordinator required. The swatch anchor is treated as inside so toggle
 * clicks are not double-handled by the outside listener.
 *
 * Transparency is treated as the optional alpha (`a`) part of the saved
 * `RgbColor` rather than a separate value: the slider's transparency is folded
 * into `rgb.a` on save so downstream consumers work purely in rgba.
 */
export const PaletteStopColorPicker: Component<PaletteStopColorPickerProps> = (
  props
) => {
  const [position, setPosition] = createSignal<PickerPosition>({
    left: 0,
    top: 0,
  });
  let pickerContainerRef: HTMLDivElement | undefined;

  // Normalized transparency: 0 = fully opaque, 1 = fully transparent (alpha = 1 - transparency).
  // Props remain the source of truth; local state only holds a user slider draft.
  const colorTransparency = (): number => 1 - tinycolor(props.color).getAlpha();
  const [transparencyOverride, setTransparencyOverride] = createSignal<
    number | undefined
  >();

  const transparency = (): number =>
    transparencyOverride() ?? colorTransparency();

  const rgbWithTransparency = (transparencyValue: number): RgbColor => {
    const rgb = tinycolor(props.color).toRgb();
    return { ...rgb, a: 1 - transparencyValue };
  };

  const handleTransparencyChange = (transparencyValue: number): void => {
    setTransparencyOverride(transparencyValue);
    props.onChange?.(rgbWithTransparency(transparencyValue));
  };

  const handleSave = (rgb: RgbColor): void => {
    props.onSave({ ...rgb, a: 1 - transparency() });
    setTransparencyOverride(undefined);
  };

  const handleClose = (): void => {
    setTransparencyOverride(undefined);
    props.onClose();
  };

  const updatePosition = (): void => {
    const anchor = props.anchorEl;
    if (!anchor) return;

    const measuredSize =
      pickerContainerRef &&
      pickerContainerRef.offsetWidth > 0 &&
      pickerContainerRef.offsetHeight > 0
        ? {
            width: pickerContainerRef.offsetWidth,
            height: pickerContainerRef.offsetHeight,
          }
        : {
            width: COLOR_PICKER_PANEL_WIDTH_PX,
            height: COLOR_PICKER_PANEL_HEIGHT_PX,
          };

    setPosition(
      computePaletteStopPickerPosition(
        anchor.getBoundingClientRect(),
        measuredSize
      )
    );
  };

  createEffect(() => {
    if (!props.show || !props.anchorEl) return;

    updatePosition();
    const frameId = requestAnimationFrame(updatePosition);
    onCleanup(() => {
      cancelAnimationFrame(frameId);
    });
  });

  createEffect(() => {
    if (!props.show) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent): void => {
      const picker = pickerContainerRef;
      const anchor = props.anchorEl;
      if (
        (picker != null && isPointerEventInsideRoot(event, picker)) ||
        (anchor != null && isPointerEventInsideRoot(event, anchor))
      ) {
        return;
      }
      handleClose();
    };

    // `mousedown` / `touchstart` (not `click`) so the DOM is stable before the
    // swatch's toggle handler runs — same pattern as `useDropdown`.
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    onCleanup(() => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    });
  });

  return (
    <Show when={props.show}>
      <Portal>
        <div
          ref={pickerContainerRef}
          class="fixed z-dropdown"
          style={{
            left: `${String(position().left)}px`,
            top: `${String(position().top)}px`,
          }}
          data-testid={props.testId}
        >
          <ColorPicker
            cssAlignment=""
            initialColor={toPickerHexColor(props.color)}
            initialTransparency={colorTransparency()}
            useTransparency={props.useTransparency}
            onTransparencyChange={handleTransparencyChange}
            onSave={handleSave}
            onClose={handleClose}
          />
        </div>
      </Portal>
    </Show>
  );
};
